<template>
  <div v-if="product" class="w-full h-full">
    <AlertDialog :open="showUpdateInvoiceStatusModal" @update:open="(o) => { if (!o) { showUpdateInvoiceStatusModal = false; selectedInvoiceId = undefined } }">
      <AlertDialogContent class="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>{{ `Update invoice ${selectedInvoiceId}` }}</AlertDialogTitle>
          <AlertDialogDescription>Update invoice status</AlertDialogDescription>
        </AlertDialogHeader>
        <div class="grid gap-3">
          <div class="grid gap-1">
            <label class="text-sm font-medium" for="update-status-product-invoice-status">Select Status</label>
            <Select v-model="updateStatus.productInvoiceStatus">
              <SelectTrigger id="update-status-product-invoice-status" class="w-full">
                <SelectValue placeholder="Select Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in statusTypes" :key="String(o.id)" :value="o.id">{{ o.text }}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div v-if="updateStatus.productInvoiceStatus == 'CANCELED'" class="grid gap-1">
            <label class="text-sm font-medium" for="update-status-cancellation-reason">Cancellation reason</label>
            <Input id="update-status-cancellation-reason" v-model="updateStatus.cancellationReason" placeholder="A reason why you are cancelling this invocie" />
          </div>
        </div>
        <AlertDialogFooter class="gap-2">
          <AlertDialogCancel @click="() => { showUpdateInvoiceStatusModal = false; selectedInvoiceId = undefined }">Cancel</AlertDialogCancel>
          <AlertDialogAction :disabled="!updateStatus.productInvoiceStatus" @click="() => { showUpdateInvoiceStatusModal = false; updateInvoice() }">Update</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <!-- Right-side form drawer: this is a form with a running invoice preview, so it reads
         better in a side panel than a centre dialog. -->
    <Drawer direction="right" :open="showCreateInvoiceModal" @update:open="(o) => !o && (showCreateInvoiceModal = false)">
      <DrawerContent class="sm:max-w-lg">
        <DrawerHeader class="text-left">
          <DrawerTitle>{{ `Purchase order for ${product.productName}` }}</DrawerTitle>
          <DrawerDescription>
            Once submitted, Boogi's team will review this order manually and get in touch via the
            established channels.
          </DrawerDescription>
        </DrawerHeader>
        <div class="flex flex-col gap-3 overflow-y-auto px-4 flex-1">
          <div class="grid gap-1">
            <!-- Read-only, so the label names the value rather than asking for input. -->
            <label class="text-sm font-medium" for="invoice-request-product-id">Perk ID</label>
            <Input id="invoice-request-product-id" v-model="invoiceRequest.productId" disabled placeholder="Perk ID" />
          </div>

          <div v-if="product.isValueSetOnOrder" class="grid gap-1">
            <label class="text-sm font-medium" for="invoice-request-unit-value-1">Value of voucher</label>
            <Input id="invoice-request-unit-value-1" v-model="invoiceRequest.unitValue" type="number" placeholder="Number to purchase" :min="product.lowerBound" :max="product.upperbound" />
          </div>
          <div v-else class="grid gap-1">
            <label class="text-sm font-medium" for="invoice-request-unit-value-2">Value (£) is preset by provider</label>
            <Input id="invoice-request-unit-value-2" v-model="invoiceRequest.unitValue" disabled type="number" placeholder="Value you want to give to end users" />
          </div>

          <div v-if="product.isRanged" class="grid gap-1">
            <label class="text-sm font-medium" for="invoice-request-quantity-1">{{ `Enter quantity between ${product.lowerBound} - ${product.upperbound}` }}</label>
            <Input id="invoice-request-quantity-1" v-model="invoiceRequest.quantity" type="number" placeholder="Number to purchase" :min="product.lowerBound" :max="product.upperbound" />
          </div>
          <div v-else class="grid gap-1">
            <label class="text-sm font-medium" for="invoice-request-quantity-2">Enter a quantity greater than 0</label>
            <Input id="invoice-request-quantity-2" v-model="invoiceRequest.quantity" :min="1" type="number" placeholder="Number to purchase" />
          </div>

      <!-- Kept inside the scrollable region so the preview scrolls with the form. -->
      <div class="mt-2">
        <div v-if="!product.isValueSetOnOrder">
          <div
            v-if="invoiceRequest.quantity && invoiceRequest.unitValue"
            class="mt-4 rounded-lg border border-border bg-card p-4 shadow-sm"
          >
            <h3 class="text-md font-semibold text-foreground mb-3">Invoice Preview</h3>

            <div class="grid grid-cols-1 gap-4">
              <!-- Left column -->
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-muted-foreground">Product Name</span>
                  <span class="font-mono text-foreground">{{ product.productName }}</span>
                </div>

                <div class="flex justify-between">
                  <span class="text-muted-foreground">Product Id</span>
                  <span class="text-foreground">{{ product.id }}</span>
                </div>

                <div class="flex justify-between">
                  <span class="text-muted-foreground">Voucher value</span>
                  <span class="text-foreground">£{{ invoiceRequest.unitValue.toFixed(2) }}</span>
                </div>

                <div class="flex justify-between">
                  <span class="text-muted-foreground">Quantity</span>
                  <span class="text-foreground">{{ invoiceRequest.quantity }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-muted-foreground">Discount</span>
                  <span class="text-foreground">
                    {{
                      (
                        ((invoiceRequest.unitValue - product.publisherPerUnitPrice) /
                          invoiceRequest.unitValue) *
                        100
                      ).toFixed(2)
                    }}%
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-muted-foreground">Price per unit</span>
                  <span class="text-foreground">£{{ product.publisherPerUnitPrice.toFixed(2) }}</span>
                </div>
              </div>

              <!-- Right column -->
              <div class="">
                <div class="flex justify-between pt-2 border-t border-border">
                  <span class="font-semibold text-muted-foreground">Total price</span>
                  <span class="font-semibold text-foreground"
                    >£{{ invoiceRequest.quantity * product.publisherPerUnitPrice }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <div
            v-if="invoiceRequest.quantity && invoiceRequest.unitValue"
            class="mt-4 rounded-lg border border-border bg-card p-4 shadow-sm"
          >
            <h3 class="text-lg font-semibold text-foreground mb-3">Invoice Preview</h3>

            <div class="grid grid-cols-1 gap-4">
              <!-- Left column -->
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-muted-foreground">Product Name</span>
                  <span class="font-mono text-foreground">{{ product.productName }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-muted-foreground">Product Id</span>
                  <span class="font-mono text-foreground">{{ product.id }}</span>
                </div>

                <div class="flex justify-between">
                  <span class="text-muted-foreground">Voucher value</span>
                  <span class="text-foreground">£{{ invoiceRequest.unitValue.toFixed(2) }}</span>
                </div>

                <div class="flex justify-between">
                  <span class="text-muted-foreground">Quantity</span>
                  <span class="text-foreground">{{ invoiceRequest.quantity }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-muted-foreground">Discount</span>
                  <span class="text-foreground"> {{ product.publisherDiscount.toFixed(2) }}% </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-muted-foreground">Price per unit</span>
                  <span class="text-foreground"
                    >£{{
                      invoiceRequest.unitValue * (1 - product.publisherDiscount / 100).toFixed(2)
                    }}</span
                  >
                </div>
              </div>

              <!-- Right column -->
              <div class="">
                <div class="flex justify-between pt-2 border-t border-border">
                  <span class="font-medium text-muted-foreground">Total price</span>
                  <span class="font-semibold text-foreground"
                    >£{{
                      (
                        invoiceRequest.quantity *
                        (invoiceRequest.unitValue * (1 - product.publisherDiscount / 100))
                      ).toFixed(2)
                    }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
        </div>
        <DrawerFooter class="flex-row justify-end gap-2">
          <Button variant="outline" @click="showCreateInvoiceModal = false">Cancel</Button>
          <Button
            :disabled="!(invoiceRequest.productId && invoiceRequest.unitValue && invoiceRequest.quantity)"
            @click="createInvoice()"
          >Submit order for review</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    <!-- The trail lives in the top bar (see setBreadcrumb) rather than being repeated in the
         page body alongside a duplicate title. -->
    <section class="mb-4">
      <div class="flex items-center gap-4">
        <div class="size-20 shrink-0 rounded-xl border border-border bg-white shadow-sm flex items-center justify-center overflow-hidden">
          <img v-if="product.retailerLogo" :src="product.retailerLogo" class="max-h-full max-w-full object-contain" alt="" />
        </div>
        <div class="min-w-0">
          <h2 class="text-xl font-semibold tracking-tight truncate">{{ product.productName }}</h2>
          <p class="text-sm text-muted-foreground truncate">{{ product.retailerName }}</p>
        </div>
      </div>
    </section>

    <Card>
      <CardHeader>
        <CardTitle>Perk profile</CardTitle>
        <CardDescription>Overview of key perk information</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="productHeaders"
          :items="[product]"
          :loading="loading"
          :error="productError"
          @retry="getProduct()"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          :hide-footer="true"
        >
          <template #item-id="{ id }">
            <CopyId :value="id" :max-width="120" />
          </template>

          <!-- Category and type share a single Details column. -->
          <template #item-details="{ category, type }">
            <div class="flex flex-wrap gap-1 items-center">
              <Badge v-if="category" variant="secondary">{{ category }}</Badge>
              <Badge v-if="type" variant="secondary">{{ type }}</Badge>
              <span v-if="!category && !type" class="text-muted-foreground">—</span>
            </div>
          </template>

          <!-- Suppliers arrive as short codes (ELLO, …); show the trading name and link out. -->
          <template #item-supplier="{ supplier }">
            <a
              v-if="supplier && supplierUrl(supplier)"
              :href="supplierUrl(supplier)"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm text-primary underline-offset-2 hover:underline whitespace-nowrap"
            >{{ supplierName(supplier) }}</a>
            <span v-else-if="supplier" class="text-sm whitespace-nowrap">{{ supplierName(supplier) }}</span>
            <span v-else class="text-muted-foreground">—</span>
          </template>

          <template #item-retailer="product">
            <div class="flex items-center gap-2.5">
              <img v-if="product.retailerLogo" :src="product.retailerLogo" class="w-8 h-8 rounded-md border border-border object-contain shrink-0 bg-white" alt="" />
              <span class="text-sm font-medium truncate" :title="product.retailerName">{{ product.retailerName }}</span>
            </div>
          </template>

          <template #item-minimumOrder="product">
            <span v-if="product.isRanged" class="text-sm tabular-nums whitespace-nowrap">{{ product.lowerBound }} - {{ product.upperbound }}</span>
            <span v-else class="text-sm tabular-nums whitespace-nowrap">{{ product.minimum }}</span>
          </template>

          <template #item-publisherPerUnitPrice="product">
            <span v-if="product.isValueSetOnOrder" class="text-sm text-muted-foreground whitespace-nowrap">Set on order</span>
            <span v-else class="text-sm tabular-nums whitespace-nowrap">£{{ product.publisherPerUnitPrice.toFixed(2) }}</span>
          </template>

          <template #item-value="product">
            <span v-if="product.isValueSetOnOrder" class="text-sm text-muted-foreground whitespace-nowrap">Set on order</span>
            <span v-else class="text-sm tabular-nums whitespace-nowrap">£{{ product.value.toFixed(2) }}</span>
          </template>

          <template #item-discount="product">
            <span v-if="product.isValueSetOnOrder" class="text-sm tabular-nums whitespace-nowrap">{{ product.publisherDiscount }}%</span>
            <span v-else class="text-sm tabular-nums whitespace-nowrap">
              {{
                (((product.value - product.publisherPerUnitPrice) / product.value) * 100).toFixed(
                  2
                )
              }}%
            </span>
          </template>

          <!-- <template #item-offerRequested="retailers">
                        <div class="flex justify-start items-center gap-1">
                            {{retailers.programOffers.length}}/{{retailers.offersList.length }}
                        </div>
                    </template>
                    <template #item-offerApproved="retailers">
                        <div class="flex justify-start items-center gap-1">
                           {{retailers.programOffers.filter(offer => offer.offerApproval == "APPROVED").length }}/ {{retailers.offersList.length }}
                        </div>
                    </template> -->
        </DataTable>
      </CardContent>
    </Card>

    <Card class="mt-4">
      <CardHeader class="flex-row items-start justify-between gap-4 space-y-0">
        <div class="flex-1">
          <CardTitle>Invoices</CardTitle>
          <CardDescription>View item invoices</CardDescription>
        </div>
        <div class="flex items-center gap-2">
          <Button size="sm" :disabled="downloading" @click="showCreateInvoiceModal = true"><Plus class="w-4 h-4" />Create new Invoice</Button>
        </div>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headersTable1"
          :items="invoices"
          :loading="loading"
          :error="error"
          @retry="getInvoices(serverOptions.page - 1, serverOptions.rowsPerPage)"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
        >
          <template #item-id="{ id }">
            <CopyId :value="id" :max-width="120" />
          </template>

          <template #item-accountingInvoiceId="{ accountingInvoiceId }">
            <CopyId v-if="accountingInvoiceId" :value="accountingInvoiceId" :max-width="120" />
            <span v-else class="text-muted-foreground">—</span>
          </template>

          <template #item-productInvoiceStatus="{ productInvoiceStatus }">
            <Badge :variant="statusVariant(productInvoiceStatus)" class="whitespace-nowrap">{{ productInvoiceStatus }}</Badge>
          </template>

          <!-- Price Formatting Slots -->
          <template #item-publisherPerUnitPrice="{ publisherPerUnitPrice }">
            <span class="text-sm tabular-nums whitespace-nowrap">£{{ Number(publisherPerUnitPrice || 0).toFixed(2) }}</span>
          </template>

          <template #item-purchasablePerUnitPrice="{ purchasablePerUnitPrice }">
            <span class="text-sm tabular-nums whitespace-nowrap">£{{ Number(purchasablePerUnitPrice || 0).toFixed(2) }}</span>
          </template>

          <template #item-expectedInvoiceAmountFromSupplier="{ expectedInvoiceAmountFromSupplier }">
            <span class="text-sm tabular-nums whitespace-nowrap">£{{ Number(expectedInvoiceAmountFromSupplier || 0).toFixed(2) }}</span>
          </template>

          <template #item-boogiRevenue="{ boogiRevenue }">
            <span class="text-sm tabular-nums whitespace-nowrap">£{{ Number(boogiRevenue || 0).toFixed(2) }}</span>
          </template>

          <template #item-unitValue="{ unitValue }">
            <span class="text-sm tabular-nums whitespace-nowrap">£{{ Number(unitValue || 0).toFixed(2) }}</span>
          </template>

          <template #item-invoicedAmountToPublisher="{ invoicedAmountToPublisher }">
            <span class="text-sm tabular-nums whitespace-nowrap">£{{ Number(invoicedAmountToPublisher || 0).toFixed(2) }}</span>
          </template>

          <!-- <template #item-offerRequested="retailers">
                        <div class="flex justify-start items-center gap-1">
                            {{retailers.programOffers.length}}/{{retailers.offersList.length }}
                        </div>
                    </template>
                    <template #item-offerApproved="retailers">
                        <div class="flex justify-start items-center gap-1">
                           {{retailers.programOffers.filter(offer => offer.offerApproval == "APPROVED").length }}/ {{retailers.offersList.length }}
                        </div>
                    </template> -->

          <template #item-action="retailers">
            <div class="flex justify-end items-center gap-1">
              <Button size="sm" @click="() => { showUpdateInvoiceStatusModal = true; selectedInvoiceId = retailers.id }">Update status</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>

    <div class="grid grid-cols-12 gap-4 mt-4">
      <div class="col-span-12">
        <Card>
          <CardHeader>
            <CardTitle>Display information</CardTitle>
            <CardDescription>Preview the information returned by our offers APIs</CardDescription>
          </CardHeader>
          <CardContent>
            <div class="rounded-xl bg-muted/40 pt-3 pb-4">
              <!--name-->
              <!--header-->
              <div class="px-2 grid gap-2 mx-auto max-w-[400px]">
                <div>
                  <img
                    class="object-contain w-full h-full rounded-lg border border-border bg-white"
                    :src="product.productLogo"
                  />
                </div>

                <div
                  class="flex justify-start items-center px-1 gap-2 w-full h-[60px] bg-card text-card-foreground shadow rounded-xl border border-border"
                >
                  <img
                    class="object-contain w-[50px] h-[50px] rounded-lg border border-border bg-white"
                    :src="product.retailerLogo"
                  />
                  <div class="">
                    <p class="font-semibold">{{ product.productName }}</p>
                  </div>
                </div>

                <div class="p-3 gap-2 w-full bg-card text-card-foreground shadow rounded-lg">
                  <p class="text-sm font-medium mb-1">Description</p>
                  <p class="text-sm">{{ product.productDescription }}</p>
                </div>
                <div class="p-3 gap-2 w-full bg-card text-card-foreground shadow rounded-lg">
                  <p class="text-sm font-medium mb-1">Terms and conditions</p>
                  <p class="text-sm">{{ product.termsAndConditions }}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup></script>

<script>
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
  Button, Input, Badge, CopyId,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
} from '@boogi/ui'
import { Plus } from 'lucide-vue-next'
import { DataTable } from '@boogi/ui'
import { breadcrumbState } from '@/stores/breadcrumb'

export default {
  name: 'manage-publsihers',
  components: {
    Card, CardHeader, CardTitle, CardDescription, CardContent,
    Button, Input, Badge, CopyId,
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
    Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
    Plus,
    DataTable,
  },
  props: {},

  data() {
    return {
      selectedInvoiceId: undefined,

      statusTypes: [{ id: 'CANCELED', text: 'CANCELED' }],

      updateStatus: {
        productInvoiceStatus: 'INVOICE_CREATED',
        cancellationReason: undefined,
        accountingInvoiceId: undefined,
      },

      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },

      showUpdateInvoiceStatusModal: false,

      invoiceRequest: {
        productId: this.$route.params.productId,
        quantity: undefined,
        unitValue: undefined,
      },

      serverItemsLength: undefined,
      downloading: false,
      productId: this.$route.params.productId,

      showCreateInvoiceModal: false,

      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      loading: true,
      error: false,
      productError: false,

      productHeaders: [
        { text: 'Id', value: 'id' },
        { text: 'Details', value: 'details' },
        { text: 'Supplier', value: 'supplier' },

        { text: 'Minimum order', value: 'minimumOrder' },
        { text: 'Voucher value', value: 'value' },

        { text: 'Per unit price', value: 'publisherPerUnitPrice' },
        { text: 'Discount', value: 'discount' },

        { text: 'Currency', value: 'currency' },
      ],

      headersTable1: [
        { text: 'Order Id', value: 'id' },
        { text: 'Accounting Id', value: 'accountingInvoiceId' },
        { text: 'Status', value: 'productInvoiceStatus' },
        { text: 'Voucher value', value: 'unitValue' },
        { text: 'Quantity', value: 'quantity' },
        { text: 'Unit price', value: 'publisherPerUnitPrice' },
        { text: 'Invoice value', value: 'invoicedAmountToPublisher' },
        { text: 'Action', value: 'action' },
      ],
      // Perk suppliers keyed by the code the API returns; unknown codes fall back to
      // the code so nothing disappears when a new supplier is added server-side.
      perkSuppliers: {
        ELLO: { name: 'Ello Group', url: 'https://www.ellogroup.co.uk/' },
        ELLOGROUP: { name: 'Ello Group', url: 'https://www.ellogroup.co.uk/' },
        VALUEDYNAMX: { name: 'Valuedynamx', url: 'https://www.valuedynamx.com/' },
        ACTION: { name: 'Action Solutions', url: '' },
        ACTIONSOLUTIONS: { name: 'Action Solutions', url: '' },
      },
      product: undefined,
      invoices: [],
    }
  },
  created() {
    this.getProduct()
    this.getInvoices(0, 25)
  },
  unmounted() {
    breadcrumbState.clear()
  },
  methods: {
    setBreadcrumb() {
      breadcrumbState.set([
        { label: 'Perks catalogue', to: '/perks-and-rewards/product-catalogue' },
        { label: this.product.productName },
      ])
    },
    supplierEntry(code) {
      return this.perkSuppliers[String(code).toUpperCase().replace(/[^A-Z]/g, '')] || null
    },
    supplierName(code) {
      const entry = this.supplierEntry(code)
      return (entry && entry.name) || code
    },
    supplierUrl(code) {
      const entry = this.supplierEntry(code)
      return (entry && entry.url) || ''
    },
    statusVariant(status) {
      const s = String(status || '').toUpperCase()
      if (['INVOICE_PAID', 'PAID', 'COMPLETED', 'FULFILLED', 'INVOICE_CREATED'].includes(s)) return 'success'
      if (['CANCELED', 'CANCELLED', 'FAILED', 'DECLINED', 'REJECTED'].includes(s)) return 'destructive'
      if (['PENDING', 'PROCESSING', 'IN_PROGRESS', 'DRAFT'].includes(s)) return 'warning'
      return 'secondary'
    },
    async createInvoice() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const response = await this.$apiFetch(`${apiUrl}/private/api/client/product-invoice`, {
          method: 'POST',
          body: JSON.stringify(this.invoiceRequest),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          this.getInvoices(0, 25)
          this.invoiceRequest = {
            publisherId: undefined,
            productId: this.$route.params.productId,
            quantity: undefined,
            unitValue: undefined,
          }
          this.showCreateInvoiceModal = false
        }
      } catch (error) {
        return
      }
    },

    async updateInvoice() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/product-invoice/${this.selectedInvoiceId}`,
          {
            method: 'POST',
            body: JSON.stringify(this.updateStatus),
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.getInvoices(0, 25)
          ;((this.updateStatus = {
            productInvoiceStatus: 'INVOICE_CREATED',
            cancellationReason: undefined,
            accountingInvoiceId: undefined,
          }),
            (this.showCreateInvoiceModal = false))
        }
      } catch (error) {
        return
      }
    },

    async getProduct() {
      this.loading = true
      this.productError = false
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/product-catalogue/${this.productId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.product = object
          this.setBreadcrumb()
          if (!this.product.isValueSetOnOrder) {
            this.invoiceRequest.unitValue = this.product.value
          }
        } else {
          this.productError = true
        }
      } catch (e) {
        this.productError = true
      } finally {
        this.loading = false
      }
    },

    async getInvoices(page, size) {
      this.loading = true
      this.error = false
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/product-invoice/product/${this.productId}?page=${page}&size=${size}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.serverItemsLength = object.totalElements
          this.invoices = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {},
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getInvoices(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
<style scoped>
.invoice-table th {
  font-size: 8px;
  width: 10px !important;
}

.invoice-table td {
  width: 130px !important;
  min-width: 130px;
  max-width: 130px;
}
</style>
